// Getting a location fix for a clock-in.
//
// ⚠️ This NEVER rejects. Every failure — permission refused, no GPS hardware,
// a timeout, an insecure origin — resolves to `null`, which the server reads as
// a remote day. That is deliberate: the alternative is someone unable to record
// a day they genuinely worked because their browser was unhappy, and the
// register matters more than the geofence.

// Long enough for a cold GPS fix indoors, short enough that nobody stares at a
// spinner wondering whether the button worked.
const TIMEOUT_MS = 8000;

/**
 * @returns {Promise<{lat, lng, accuracyMetres} | null>}
 */
export function getFix() {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    // Belt and braces: some browsers never call either callback when the
    // permission prompt is dismissed rather than answered, and a clock-in that
    // hangs for ever is worse than one recorded as remote.
    const fallback = setTimeout(() => done(null), TIMEOUT_MS + 1000);

    // ⚠️ try/catch, not just the error callback. A throw inside a Promise
    // executor REJECTS the promise, and getCurrentPosition throws synchronously
    // on an insecure origin (plain http on anything but localhost) in some
    // browsers. Without this, deploying without TLS would make clocking in
    // fail outright rather than quietly recording a remote day.
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(fallback);
          done({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            // The device's own error estimate. The server lets it count in the
            // employee's favour at the fence edge, so it is worth sending.
            accuracyMetres: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
          });
        },
        () => {
          clearTimeout(fallback);
          done(null);
        },
        {
          enableHighAccuracy: true,
          timeout: TIMEOUT_MS,
          // A fix from the last minute is fine and saves a cold start; older
          // than that could be this morning's location from another building.
          maximumAge: 60000,
        }
      );
    } catch {
      clearTimeout(fallback);
      done(null);
    }
  });
}

/** Has the user already granted location, so we can skip explaining the prompt? */
export async function permissionState() {
  try {
    if (!navigator?.permissions?.query) return 'unknown';
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state; // 'granted' | 'denied' | 'prompt'
  } catch {
    return 'unknown';
  }
}
